/**
 * SlabView — Full-screen card viewer with:
 *   · PSA/BGS/CGC slab chrome for graded cards
 *   · Clean floating frame for raw cards
 *   · 3D tilt via mouse / finger drag (framer-motion spring)
 *   · Info bottom-sheet with card facts
 */

import { useState, useRef, useCallback } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import type { LiveCard } from "../hooks/usePokemonTCG";
import { RARITY_LABEL, RARITY_COLOR } from "./PokemonCard";
import "./SlabView.scss";

// ─── Grade labels ─────────────────────────────────────────────────────────────
const PSA_LABELS: Record<number, string> = {
  10: "GEM MT", 9: "MINT", 8.5: "NM-MT+", 8: "NM-MT",
  7.5: "NM+",   7: "NM",   6.5: "EX-MT+", 6: "EX-MT",
  5.5: "EX+",   5: "EX",   4.5: "VG-EX+", 4: "VG-EX",
  3.5: "VG+",   3: "VG",   2.5: "GOOD+",  2: "GOOD",
  1.5: "FR",    1: "P",
};
function gradeLabel(g: number): string {
  return PSA_LABELS[g] ?? `GR ${g}`;
}

// ─── Card facts generator ─────────────────────────────────────────────────────
function buildFacts(card: LiveCard): Array<{ title: string; body: string }> {
  const facts: Array<{ title: string; body: string }> = [];

  facts.push({
    title: `From "${card.setName}"`,
    body: `Card #${card.number} pulled from the ${card.setName} set. Sets rotate in BESC Draw pools regularly to keep things fresh.`,
  });

  const rarityDesc: Partial<Record<string, string>> = {
    common:   "Common cards form the backbone of every set — useful and plentiful, but occasionally hide strong utility plays.",
    uncommon: "Uncommon cards sit above commons in pull rate and often feature powerful supporting Pokémon.",
    rare:     "Rares are harder to land and typically feature fan-favourite Pokémon with compelling artwork.",
    holo:     "Holo Rares feature a reflective holographic foil pattern that makes them shimmer under light — a staple of serious collections.",
    ultra:    "Ultra Rares are the chase cards of their set: full-art prints, special illustrations, or secret rares that command significant collector demand.",
    graded:   "Professionally graded cards have been reviewed, authenticated, and sealed in a tamper-evident case. Grading preserves condition and can significantly increase market value.",
  };
  facts.push({
    title: `${RARITY_LABEL[card.rarity]} · Rarity`,
    body: rarityDesc[card.rarity] ?? "A special card from the Pokémon TCG.",
  });

  if (card.isGraded && card.grade != null) {
    const co  = card.gradingCompany ?? "PSA";
    const lbl = gradeLabel(card.grade);
    const body =
      card.grade === 10
        ? `A ${co} 10 Gem Mint is the highest possible grade. No flaws visible to the naked eye — perfect centering, sharp corners, clean edges, and an unblemished surface.`
        : card.grade >= 9
        ? `A ${co} ${card.grade} (${lbl}) indicates a near-perfect specimen with only the most minor imperfections. Highly sought after by collectors.`
        : `${co} ${card.grade} (${lbl}) — professionally authenticated and sealed in a protective acrylic slab, ensuring long-term preservation.`;
    facts.push({ title: `${co} ${card.grade} — ${lbl}`, body });
  }

  if (card.isAltArt) {
    facts.push({
      title: "Special Illustration Rare",
      body:  "SIRs and Alt Arts feature full-bleed, artist-commissioned illustrations that reimagine iconic Pokémon — the pinnacle of modern Pokémon TCG aesthetics.",
    });
  }

  if (card.approxValueUsd > 0) {
    facts.push({
      title: "Market Value",
      body: `Currently valued at approximately $${card.approxValueUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD based on live TCGPlayer market data.${card.isGraded ? " Graded copies typically command a premium over ungraded raw cards." : ""}`,
    });
  }

  return facts;
}

// ─── Component ────────────────────────────────────────────────────────────────
interface SlabViewProps {
  card:    LiveCard;
  onClose: () => void;
  /** Optional small hint shown below the price, e.g. "Can drop in the Starter Pack" */
  hint?:   string;
}

export function SlabView({ card, onClose, hint }: SlabViewProps) {
  const [showInfo,    setShowInfo]    = useState(false);
  const [imgLoaded,   setImgLoaded]   = useState(false);
  const slabRef = useRef<HTMLDivElement>(null);

  // ── Spring-tilt ─────────────────────────────────────────────────────────────
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const cfg  = { stiffness: 130, damping: 18 };
  const spX  = useSpring(rawX, cfg);
  const spY  = useSpring(rawY, cfg);

  const rotateY = useTransform(spX, [-0.5, 0.5], [-22, 22]);
  const rotateX = useTransform(spY, [-0.5, 0.5], [16, -16]);

  // Glare radial
  const glareBackground = useTransform(
    [spX, spY],
    ([x, y]: number[]) =>
      `radial-gradient(circle at ${((x as number) + 0.5) * 100}% ${
        ((y as number) + 0.5) * 100
      }%, rgba(255,255,255,0.26) 0%, transparent 52%)`
  );

  // Edge highlight (left→right shine)
  const edgeShine = useTransform(spX, [-0.5, 0.5], [
    "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, transparent 30%)",
    "linear-gradient(270deg, rgba(255,255,255,0.12) 0%, transparent 30%)",
  ]);

  const updateTilt = useCallback((cx: number, cy: number) => {
    if (!slabRef.current) return;
    const r = slabRef.current.getBoundingClientRect();
    rawX.set((cx - r.left)  / r.width  - 0.5);
    rawY.set((cy - r.top)   / r.height - 0.5);
  }, [rawX, rawY]);

  const handleMouseMove  = useCallback((e: React.MouseEvent)  => updateTilt(e.clientX, e.clientY), [updateTilt]);
  const handleTouchMove  = useCallback((e: React.TouchEvent)  => {
    const t = e.touches[0]; if (t) updateTilt(t.clientX, t.clientY);
  }, [updateTilt]);
  const resetTilt = useCallback(() => { rawX.set(0); rawY.set(0); }, [rawX, rawY]);

  const isGraded = card.isGraded && !!card.gradingCompany;
  const gradeCo  = card.gradingCompany ?? "PSA";
  const facts    = buildFacts(card);

  // Year from set name heuristic
  const setYear = (() => {
    const m = card.setName.match(/20\d\d/);
    return m ? m[0] : new Date().getFullYear().toString();
  })();

  return (
    <motion.div
      className="SV"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
    >
      {/* Dark bg — click outside closes info sheet, double-click closes SV */}
      <div className="SV__bg" onClick={() => showInfo ? setShowInfo(false) : onClose()} />

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="SV__topbar">
        <button className="SV__topbtn" onClick={onClose} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="SV__topbtn" onClick={(e) => { e.stopPropagation(); setShowInfo(true); }}
          aria-label="Info">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M10 9v6M10 6.5v.5" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* ── Slab stage ──────────────────────────────────────────────────────── */}
      <div className="SV__stage">
        <motion.div
          ref={slabRef}
          className={`SV__slab SV__slab--${isGraded ? gradeCo.toLowerCase() : "raw"}`}
          style={{ rotateX, rotateY, transformPerspective: 1100 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={resetTilt}
          onTouchMove={handleTouchMove}
          onTouchEnd={resetTilt}
          initial={{ scale: 0.82, opacity: 0, y: 30 }}
          animate={{ scale: 1,    opacity: 1, y: 0  }}
          transition={{ type: "spring", stiffness: 220, damping: 22, delay: 0.06 }}
        >
          {/* PSA / BGS / CGC label */}
          {isGraded && (
            <div className={`SV__label SV__label--${gradeCo.toLowerCase()}`}>
              {/* Left column */}
              <div className="SV__label-main">
                <div className="SV__label-year-set">
                  {setYear} {card.setName.toUpperCase().slice(0, 28)}
                </div>
                <div className="SV__label-cardname">{card.name.toUpperCase()}</div>
                <div className="SV__label-sub">
                  {RARITY_LABEL[card.rarity].toUpperCase()}
                  {card.number ? `   #${card.number}` : ""}
                </div>
                <div className="SV__label-barcode">
                  <div className="SV__barcode" />
                </div>
              </div>
              {/* Right column — grade */}
              <div className="SV__label-grade-block">
                <div className="SV__label-grading-co">{gradeCo}</div>
                <div className="SV__label-grade-num">{card.grade}</div>
                <div className="SV__label-grade-text">
                  {gradeLabel(card.grade ?? 10)}
                </div>
              </div>
            </div>
          )}

          {/* Card inside slab */}
          <div className={`SV__card-well${isGraded ? "" : " SV__card-well--raw"}`}>
            {!imgLoaded && <div className="SV__card-skeleton" />}
            <motion.img
              className={`SV__card-img${imgLoaded ? " loaded" : ""}`}
              src={card.imageLarge ?? card.imageSmall}
              alt={card.name}
              onLoad={() => setImgLoaded(true)}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.src !== card.imageSmall) {
                  img.src = card.imageSmall;
                } else {
                  setImgLoaded(true);
                }
              }}
            />

            {/* Glare overlay */}
            <motion.div className="SV__glare" style={{ background: glareBackground }} />

            {/* Holographic shimmer for special rarity */}
            {(card.rarity === "ultra" || card.rarity === "graded" || card.isAltArt) && (
              <div className="SV__holo-layer" />
            )}
          </div>

          {/* Acrylic edge reflections */}
          <motion.div className="SV__acrylic-edge SV__acrylic-edge--top"    style={{ background: edgeShine }} />
          <div className="SV__acrylic-edge SV__acrylic-edge--left"  />
          <div className="SV__acrylic-edge SV__acrylic-edge--right" />
          <div className="SV__acrylic-edge SV__acrylic-edge--bottom"/>
        </motion.div>
      </div>

      {/* ── Caption ─────────────────────────────────────────────────────────── */}
      <motion.div
        className="SV__caption"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18 }}
      >
        <div className="SV__caption-price">
          ${card.approxValueUsd >= 1000
            ? (card.approxValueUsd / 1000).toFixed(2) + "k"
            : card.approxValueUsd.toFixed(2)}
        </div>
        <div className="SV__caption-name">{card.name}</div>
        {hint && <p className="SV__caption-hint">{hint}</p>}
      </motion.div>

      {/* ── Info bottom sheet ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            className="SV__sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle / close pill */}
            <div className="SV__sheet-handle" onClick={() => setShowInfo(false)} />

            <div className="SV__sheet-body">
              <h2 className="SV__sheet-name">{card.name}</h2>
              <p className="SV__sheet-set">{card.setName}</p>

              {isGraded && card.grade != null && (
                <span className={`SV__grade-badge SV__grade-badge--${gradeCo.toLowerCase()}`}>
                  {gradeCo} Grade: <strong>{card.grade}.0</strong>
                </span>
              )}

              {!isGraded && (
                <span className="SV__grade-badge SV__grade-badge--raw"
                  style={{ borderColor: RARITY_COLOR[card.rarity], color: RARITY_COLOR[card.rarity] }}>
                  {RARITY_LABEL[card.rarity]}
                </span>
              )}

              <h3 className="SV__sheet-section-title">Card Facts</h3>
              <hr className="SV__sheet-divider" />

              {facts.map((f, i) => (
                <div key={i} className="SV__fact">
                  <div className="SV__fact-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
                      <path d="M8 7v5M8 4.5v.5" stroke="currentColor" strokeWidth="1.6"
                        strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="SV__fact-content">
                    <strong>{f.title}</strong>
                    <p>{f.body}</p>
                  </div>
                </div>
              ))}

              <p className="SV__sheet-footer">
                Powered by BESC Draw · Market data via TCGPlayer
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
